import { ChangeEvent, useState } from "react";
import _ from "lodash";

export interface UseOcFormEditorSelectorParams<T> {
  list: T[];
  value?: string;
  selectName?: boolean;
  searchCode?: boolean;
}

export interface UseOcFormEditorSelectorResult<T> {
  list: T[];
  search: string;
  setSearch: (val: string) => void;
  selectedItem?: T;
  handleClearSearch: () => void;
  handleSearch: (e: ChangeEvent<HTMLInputElement>) => void;
  handleHasMore: () => void;
}

/**
 * Hook for searchable, infinite-scrolling selector.
 *
 * @param list - Array of items with at least `code` and `name` fields.
 * @param value - Currently selected code or name.
 * @param selectName - If true, selection matches `name` instead of `code`.
 * @param searchCode - If true, search also matches `code` field.
 */
export function useOcFormEditorSelector<
  T extends { code: string; name: string },
>({
  list,
  value,
  selectName = false,
  searchCode = false,
}: UseOcFormEditorSelectorParams<T>): UseOcFormEditorSelectorResult<T> {
  const [search, setSearch] = useState<string>("");

  // Determine selected item by code or name
  let selectedItem = list.find((item) =>
    selectName ? item.name === value : item.code === value,
  );

  // Build regex from search term (escape special chars)
  const regex = new RegExp(search, "i");
  // Filter list based on name or code
  const searchedList = _.filter(list, (item) =>
    searchCode ? regex.test(item.code) : regex.test(item.name),
  );

  // Infinite scroll slicing
  const initialListLength = 25;
  const [listLength, setListLength] = useState<number>(initialListLength);
  const listForInfiniteScrolling = searchedList.slice(0, listLength);

  // Handlers
  const handleSearch = (e: ChangeEvent<HTMLInputElement>): void => {
    const sanitized = e.target.value.replaceAll(/[.,@*+?^${}()|[\]\\]/g, " ");
    setSearch(sanitized);
  };

  const handleClearSearch = (): void => {
    setSearch("");
  };

  const handleHasMore = (): void => {
    setListLength((prev) => prev + initialListLength);
  };

  return {
    list: listForInfiniteScrolling,
    search,
    setSearch,
    selectedItem,
    handleClearSearch,
    handleSearch,
    handleHasMore,
  };
}

export default useOcFormEditorSelector;
